// Ingest workspace store (§5/§12.4). Owns the on-disk workspace: atomic writes
// (same-dir temp + fsync + rename), pass directories, manifest and run_state.
// All JSON is written atomically so a crash never leaves a half-written product.

import * as fs from "node:fs";
import * as path from "node:path";

export function atomicWrite(filePath: string, data: string | Buffer): void {
  const dir = path.dirname(filePath);
  fs.mkdirSync(dir, { recursive: true });
  const tmp = path.join(dir, `.${path.basename(filePath)}.${process.pid}.tmp`);
  const fd = fs.openSync(tmp, "w");
  try {
    fs.writeFileSync(fd, data);
    fs.fsyncSync(fd);
  } finally {
    fs.closeSync(fd);
  }
  fs.renameSync(tmp, filePath);
}

export function writeJson(filePath: string, value: unknown): void {
  atomicWrite(filePath, JSON.stringify(value, null, 2) + "\n");
}

export function readJson<T = unknown>(filePath: string): T {
  return JSON.parse(fs.readFileSync(filePath, "utf-8")) as T;
}

export function exists(filePath: string): boolean {
  return fs.existsSync(filePath);
}

export class WorkspaceStore {
  constructor(readonly root: string) {}

  private abs(rel: string): string {
    return path.join(this.root, rel);
  }

  ensure(): void {
    fs.mkdirSync(this.root, { recursive: true });
  }

  passDir(name: string): string {
    const dir = this.abs(name);
    fs.mkdirSync(dir, { recursive: true });
    return dir;
  }

  readText(rel: string): string {
    return fs.readFileSync(this.abs(rel), "utf-8");
  }

  writeText(rel: string, data: string): void {
    atomicWrite(this.abs(rel), data);
  }

  writeJson(rel: string, value: unknown): void {
    writeJson(this.abs(rel), value);
  }

  readJson<T = unknown>(rel: string): T {
    return readJson<T>(this.abs(rel));
  }

  has(rel: string): boolean {
    return fs.existsSync(this.abs(rel));
  }

  path(rel: string): string {
    return this.abs(rel);
  }

  // Remove a unit's stale error sidecar after it succeeds (§5.3).
  clearError(rel: string): void {
    const p = this.abs(rel);
    if (fs.existsSync(p)) fs.rmSync(p);
  }
}
